<template>
  <div ref="editorContainer" class="code-editor-cm" />
</template>

<script>
import { EditorView, keymap, lineNumbers, highlightActiveLine } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { indentOnInput, bracketMatching, foldGutter, syntaxHighlighting, defaultHighlightStyle } from '@codemirror/language';
import { html } from '@codemirror/lang-html';
import { css } from '@codemirror/lang-css';
import { javascript } from '@codemirror/lang-javascript';
import { oneDark } from '@codemirror/theme-one-dark';

const languageMap = {
  html,
  css,
  javascript,
};

export default {
  name: 'CodeEditor',

  props: {
    value: {
      type: String,
      default: '',
    },
    language: {
      type: String,
      default: 'html',
      validator: (val) => ['html', 'css', 'javascript'].includes(val),
    },
    darkTheme: {
      type: Boolean,
      default: false,
    },
    height: {
      type: String,
      default: '300px',
    },
    // soft-wrap long lines instead of scrolling horizontally
    lineWrapping: {
      type: Boolean,
      default: false,
    },
    // ms to wait before emitting `input`; 0 emits on every change (like a textarea)
    debounce: {
      type: Number,
      default: 300,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      view: null,
      internalUpdate: false,
      debounceTimer: null,
    };
  },

  watch: {
    value(newVal) {
      if (!this.view || this.internalUpdate) return;
      const current = this.view.state.doc.toString();
      if (current === newVal) return;
      this.view.dispatch({
        changes: { from: 0, to: current.length, insert: newVal },
      });
    },
    darkTheme() {
      this.rebuildEditor();
    },
    language() {
      this.rebuildEditor();
    },
    readOnly() {
      this.rebuildEditor();
    },
  },

  mounted() {
    if (!this.$refs.editorContainer) {
      return;
    }
    this.buildEditor();
  },

  beforeDestroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    if (this.view) {
      this.view.destroy();
      this.view = null;
    }
  },

  methods: {
    buildEditor() {
      if (!this.$refs.editorContainer) {
        return;
      }

      const langExtension = languageMap[this.language] ? languageMap[this.language]() : html();

      const extensions = [
        lineNumbers(),
        history(),
        foldGutter(),
        indentOnInput(),
        bracketMatching(),
        highlightActiveLine(),
        syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
        langExtension,
        keymap.of([...defaultKeymap, ...historyKeymap]),
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            // every change is emitted; the value watcher ignores it because the doc already matches
            if (this.debounce <= 0) {
              this.$emit('input', update.state.doc.toString());
              return;
            }

            if (this.debounceTimer) {
              clearTimeout(this.debounceTimer);
            }
            
            this.debounceTimer = setTimeout(() => {
              if (!this.internalUpdate && this.view) {
                this.internalUpdate = true;
                this.$emit('input', this.view.state.doc.toString());
                this.$nextTick(() => {
                  this.internalUpdate = false;
                });
              }
              this.debounceTimer = null;
            }, 300);
          }
        }),
        EditorView.theme({
          '&': { height: this.height },
          '.cm-scroller': { overflow: 'auto', fontFamily: 'Fira Code, Fira Mono, Consolas, Menlo, Courier, monospace', fontSize: '13px' },
        }),
      ];

      if (this.darkTheme) {
        extensions.push(oneDark);
      }

      if (this.lineWrapping) {
        extensions.push(EditorView.lineWrapping);
      }

      if (this.readOnly) {
        extensions.push(EditorState.readOnly.of(true), EditorView.editable.of(false));
      }

      this.view = new EditorView({
        state: EditorState.create({
          doc: this.value || '',
          extensions,
        }),
        parent: this.$refs.editorContainer,
      });
    },

    rebuildEditor() {
      const val = this.view ? this.view.state.doc.toString() : this.value;
      if (this.view) {
        this.view.destroy();
        this.view = null;
      }
      this.$nextTick(() => {
        this.buildEditor();
        if (val && this.view) {
          const current = this.view.state.doc.toString();
          if (current !== val) {
            this.view.dispatch({
              changes: { from: 0, to: current.length, insert: val },
            });
          }
        }
      });
    },
  },
};
</script>

<style scoped>
  .code-editor-cm {
    border: 1px solid #ccc;
    border-radius: 4px;
    overflow: hidden;
  }
</style>
